import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Castle, 
  Star, 
  Clock, 
  Shield, 
  Truck, 
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WHATSAPP_NUMBER = '529993607922';
const WHATSAPP_MESSAGE = encodeURIComponent('¡Hola! Me interesa rentar un inflable para mi evento.');

const features = [
  { icon: Shield, title: 'Seguro y Limpio', desc: 'Inflables sanitizados y en perfecto estado' },
  { icon: Clock, title: '6 Horas de Diversión', desc: 'Tiempo suficiente para tu evento' },
  { icon: Truck, title: 'Entrega a Domicilio', desc: 'Llevamos e instalamos en tu ubicación' },
];

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_0d3815ce-2fa6-4430-b5d2-0237895e78e6/artifacts/wq5yg206_logoAllegra.png';

const testimonials = [
  { name: 'María García', rating: 5, text: 'Excelente servicio! Los niños se divirtieron muchísimo. El inflable llegó a tiempo y en perfectas condiciones.' },
  { name: 'Carlos López', rating: 5, text: 'Muy profesionales. El Paw Patrol fue un éxito total en la fiesta de mi hijo. 100% recomendados.' },
  { name: 'Ana Martínez', rating: 5, text: 'Precios justos y atención de primera. Ya es la tercera vez que rento con ellos.' },
];

export default function HomePage() {
  const [inflables, setInflables] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInflables = async () => {
      try {
        const response = await axios.get(`${API_URL}/inflables?activo=true&orden=popularidad`);
        setInflables(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching inflables:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInflables();
  }, []);

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-allegra-light via-white to-secondary/10 py-16 lg:py-24">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge className="mb-4 bg-accent/20 text-allegra-dark border-accent">
                <Sparkles className="w-3 h-3 mr-1" />
                ¡Diversión Garantizada!
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-allegra-dark leading-tight mb-6">
                Renta de <span className="text-gradient">Inflables</span> para tu Fiesta
              </h1>
              <p className="text-lg text-allegra-gray mb-8 max-w-lg mx-auto lg:mx-0">
                Haz de tu evento infantil un momento inolvidable. 
                Tenemos los mejores brincolines con temáticas que los niños aman.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="rounded-full gap-2 text-lg px-8 shadow-lg shadow-primary/25" data-testid="hero-whatsapp-btn">
                    <MessageCircle className="w-5 h-5" />
                    Cotizar por WhatsApp
                  </Button>
                </a>
                <Link to="/catalogo">
                  <Button size="lg" variant="outline" className="rounded-full gap-2 text-lg px-8 border-2" data-testid="hero-catalogo-btn">
                    Ver Catálogo
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center justify-center lg:justify-start gap-6 text-sm text-allegra-gray">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Desde $550
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Instalación incluida
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_922fdf0c-dad0-41c3-b252-1cceb96f58e3/artifacts/1p74rpug_file_000000002ae871fd81f23a6b0fcfad8d.png" 
                  alt="Niños jugando en inflable"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl hidden lg:block animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                    <Castle className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-allegra-dark">+500</p>
                    <p className="text-sm text-allegra-gray">Eventos felices</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg card-hover" data-testid={`feature-card-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-allegra-dark mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-allegra-gray">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Inflables */}
      <section className="py-16 lg:py-24 bg-allegra-light bg-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-secondary/20 text-allegra-dark border-secondary">
              Lo más popular
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-allegra-dark mb-4">
              Nuestros Inflables Destacados
            </h2>
            <p className="text-allegra-gray max-w-2xl mx-auto">
              Los favoritos de nuestros clientes. Temáticas divertidas que harán de tu fiesta un éxito.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {inflables.map((inflable) => (
                <Link to={`/inflable/${inflable.id}`} key={inflable.id} data-testid={`inflable-card-${inflable.id}`}>
                  <Card className="overflow-hidden border-0 shadow-lg card-hover bg-white h-full">
                    <div className="relative h-56">
                      <img 
                        src={inflable.imagen_url || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600'} 
                        alt={inflable.nombre}
                        className="w-full h-full object-cover"
                      />
                      <Badge className="absolute top-4 left-4 bg-white/90 text-allegra-dark">
                        {inflable.categoria}
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-heading font-bold text-xl text-allegra-dark mb-2">
                        {inflable.nombre}
                      </h3>
                      <p className="text-allegra-gray text-sm mb-4 line-clamp-2">
                        {inflable.descripcion}
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm text-allegra-gray">Desde</span>
                          <p className="font-heading font-bold text-2xl text-primary">
                            ${inflable.precio_base}
                          </p>
                        </div>
                        <Button variant="outline" className="rounded-full">
                          Ver más
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/catalogo">
              <Button size="lg" variant="outline" className="rounded-full gap-2 text-lg px-8 border-2" data-testid="ver-todos-btn">
                Ver Todos los Inflables
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-accent/20 text-allegra-dark border-accent">
              Opiniones reales
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-allegra-dark mb-4">
              Lo que dicen nuestros clientes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-allegra-gray mb-4 italic">"{testimonial.text}"</p>
                  <p className="font-heading font-bold text-allegra-dark">{testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-primary to-primary/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            ¿Listo para hacer tu fiesta inolvidable?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Contáctanos ahora y reserva el inflable perfecto para tu evento. 
            ¡Te atendemos de inmediato!
          </p>
          <a 
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="lg" className="rounded-full gap-2 text-lg px-10 bg-white text-primary hover:bg-white/90 shadow-xl" data-testid="cta-whatsapp-btn">
              <MessageCircle className="w-5 h-5" />
              Cotizar Ahora
            </Button>
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
